<template>
    <div
        key="data-loaded"
        :class="{
            'mg-control-panel-wrapper--title-active': meetingHasTopic,
            'mg-control-panel-wrapper--active': isUiActive
        }"
        class="meeting-container mg-control-panel-wrapper min-h-screen flex flex-col flex-center items-center"
        ref="mg-control-panel-wrapper"
    >
        <div
            ref="bg-layer"
            aria-hidden="true"
            class="fixed top-0 left-0 w-screen h-screen bg-brand"
        ></div>

        <audio id="GrahamBell"></audio>

        <div
            id="video-group"
            v-if="!gridViewIsActive"
            class="w-full max-w-lg mx-auto relative flex-1 flex items-center"
        >
            <div class="p-6 w-full flex flex-wrap justify-center items-center">
                <div class="flex w-full relative p-2 md:w-1/2 lg:w-full">
                    <div
                        class="video-thumbnail--grid-item-inner relative video-thumbnail video-thumbnail--grid-item mg-filter"
                    >
                        <div
                            v-addspeakerel
                            class="h-full w-full top-0 left-0 absolute"
                        ></div>
                        <div
                            v-if="currentSpeakerData"
                            class="text-white text-xs py-1 px-3 rounded-full tracking-wide absolute bottom-0 right-0 mr-2 mb-2 mg-filter"
                        >
                            <span>{{
                                formatName(currentSpeakerData.info)
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="!meetingHasConnection"
            key="errorMessage"
            class="flex items-center justify-center h-screen w-screen fixed z-10"
        >
            <div class="notification bg-red-300 max-w-md">
                <b>Sorry</b>, there was an error establishing a connection to
                the meeting.
            </div>
        </div>

        <transition name="page-fade" mode="out-in">
            <div
                v-if="gridViewIsActive"
                class="flex flex-wrap justify-center items-center w-full z-10 relative flex-1"
            >
                <div class="grid-wrapper w-full max-w-lg mx-auto">
                    <transition-group
                        name="feed-transition"
                        tag="div"
                        enter-active-class="animated fadeInUp"
                        leave-active-class="animated fadeOut"
                        class="p-6 w-full flex flex-wrap justify-center items-center"
                        ref="tile-wrapper"
                    >
                        <div
                            v-for="(value,
                            attendeeId,
                            index) in attendeesForDisplay"
                            :key="`${attendeeId}-${index}`"
                            :class="{
                                'lg:w-1/4':
                                    4 < Object.keys(attendeesForDisplay).length,
                                'lg:w-full':
                                    1 ===
                                    Object.keys(attendeesForDisplay).length
                            }"
                            class="flex w-full relative p-2 md:w-1/2"
                        >
                            <div
                                class="video-thumbnail--grid-item-inner relative video-thumbnail video-thumbnail--grid-item mg-filter"
                            >
                                <div
                                    v-addvideoel
                                    :data-attendee-id="attendeeId"
                                    class="h-full w-full top-0 left-0 absolute"
                                ></div>

                                <div
                                    class="absolute top-0 left-0 ml-2 mt-2 text-lg py-1 px-3 rounded-full mg-filter text-white flex"
                                >
                                    <div
                                        :class="{
                                            'text-red-500': value.muted
                                        }"
                                        class="mr-3 leading-none flex items-center justify-center"
                                    >
                                        <svg-mic-off
                                            v-if="value.muted"
                                            aria-hidden="true"
                                        />
                                        <svg-mic v-else aria-hidden="true" />
                                    </div>

                                    <div
                                        :class="{
                                            'mr-3': !value.muted
                                        }"
                                        class="leading-none flex items-center justify-center"
                                    >
                                        <svgConnection
                                            v-if="75 <= value.signalStrength"
                                            aria-hidden="true"
                                        />
                                        <svgConnection2
                                            v-else-if="
                                                50 <= value.signalStrength
                                            "
                                            aria-hidden="true"
                                        />
                                        <svgConnection3
                                            v-else-if="
                                                25 <= value.signalStrength
                                            "
                                            aria-hidden="true"
                                        />
                                        <svgConnection4
                                            v-else
                                            aria-hidden="true"
                                        />
                                    </div>

                                    <div
                                        v-if="!value.muted"
                                        class="leading-none flex items-center justify-center"
                                    >
                                        <svgVolumeHigh
                                            v-if="75 <= value.volume"
                                            aria-hidden="true"
                                        />
                                        <svgVolumeMedium
                                            v-else-if="50 <= value.volume"
                                            aria-hidden="true"
                                        />
                                        <svgVolumeLow
                                            v-else-if="25 < value.volume"
                                            aria-hidden="true"
                                        />
                                        <svgVolumeMute
                                            v-else
                                            class="opacity-25"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="text-white text-xs py-1 px-3 rounded-full tracking-wide absolute bottom-0 right-0 mr-2 mb-2 mg-filter"
                                >
                                    <span>{{ formatName(value.info) }}</span>
                                </div>
                            </div>
                        </div>
                    </transition-group>
                </div>
            </div>
        </transition>

        <div
            :class="{
                'translate-x-full': gridViewIsActive
            }"
            class="swiper-meeting-participants top-0 right-0 border-0 fixed h-screen p-4 py-10 transform mg-ease duration-300 z-10"
        >
            <div
                v-if="hasVideoThumbnails"
                class="swiper-button-prev swiper-nav-vertical swiper-nav-vertical-prev hidden"
            ></div>

            <swiper
                ref="thumbswiper"
                :options="swiperOption"
                class="h-full"
                v-if="!gridViewIsActive"
            >
                <div
                    v-for="(value, attendeeId, index) in attendeesForDisplay"
                    :key="index"
                    class="flex items-end justify-end bg-black mg-filter video-thumbnail relative swiper-slide"
                >
                    <div
                        v-addvideoel
                        :data-attendee-id="attendeeId"
                        class="h-full w-full top-0 left-0 absolute"
                    ></div>

                    <div
                        class="absolute top-0 left-0 ml-2 mt-2 text-lg py-1 px-3 rounded-full mg-filter text-white flex"
                    >
                        <div
                            :class="{
                                'text-red-500': value.muted
                            }"
                            class="mr-3 leading-none flex items-center justify-center"
                        >
                            <svg-mic-off
                                v-if="value.muted"
                                aria-hidden="true"
                            />
                            <svg-mic v-else aria-hidden="true" />
                        </div>

                        <div
                            :class="{
                                'mr-3': !value.muted
                            }"
                            class="leading-none flex items-center justify-center"
                        >
                            <svgConnection
                                v-if="75 <= value.signalStrength"
                                aria-hidden="true"
                            />
                            <svgConnection2
                                v-else-if="50 <= value.signalStrength"
                                aria-hidden="true"
                            />
                            <svgConnection3
                                v-else-if="25 <= value.signalStrength"
                                aria-hidden="true"
                            />
                            <svgConnection4 v-else aria-hidden="true" />
                        </div>

                        <div
                            v-if="!value.muted"
                            class="leading-none flex items-center justify-center"
                        >
                            <svgVolumeHigh
                                v-if="66 <= value.volume"
                                aria-hidden="true"
                            />
                            <svgVolumeMedium
                                v-else-if="33 <= value.volume"
                                aria-hidden="true"
                            />
                            <svgVolumeLow
                                v-else-if="15 < value.volume"
                                aria-hidden="true"
                            />
                            <svgVolumeMute
                                v-else
                                class="opacity-25"
                                aria-hidden="true"
                            />
                        </div>
                    </div>

                    <div
                        class="text-white text-xs py-1 px-3 rounded-full tracking-wide absolute bottom-0 right-0 mr-2 mb-2 mg-filter"
                    >
                        <span class="text-white">{{
                            formatName(value.info)
                        }}</span>
                    </div>
                </div>
                <swiper-slide />
            </swiper>
            <div
                v-if="hasVideoThumbnails"
                class="swiper-button-next swiper-nav-vertical swiper-nav-vertical-next hidden"
            ></div>
        </div>

        <div
            :class="{
                'mg-filter--medium': meetingHasTopic
            }"
            class="mg-control-panel mg-control-panel--view-info w-screen fixed top-0 flex flex-wrap items-center justify-center mg-filter z-10"
        >
            <div class="p-4 md:px-16 max-w-md text-white text-center">
                <div
                    :class="{
                        'font-bold': !meetingHasTopic
                    }"
                    class="text-white mb-1 text-lg block leading-tight"
                >
                    {{ meetingTitle }}
                </div>
                <div
                    class="inline-block rounded"
                    :class="{
                        'bg-red-200': extraMinutesActive,
                        'p-2': extraMinutesActive,
                        'py-1': extraMinutesActive,
                        'text-red-700': extraMinutesActive,
                        'font-bold': extraMinutesActive
                    }"
                >
                    <span ref="meeting-minutes">{{
                        meetingMinutesDisplay
                    }}</span
                    >:<span ref="meeting-seconds">{{
                        meetingSecondsDisplay
                    }}</span>
                </div>
            </div>
        </div>

        <div
            :class="{
                '2xl:z-10 opacity-25 2xl:opacity-100': meetingHasTopic,
                'lg:z-10 opacity-25 lg:opacity-100': !meetingHasTopic
            }"
            class="fixed top-0 left-0 mt-5 ml-6 pointer-events-none"
        >
            <img
                v-if="brandLogo"
                :src="brandLogo"
                class="block meeting-logo"
                alt=""
            />
        </div>

        <div
            :class="{
                active: activeUiElements.includes(
                    'mg-control-panel--secondary-options'
                )
            }"
            class="mg-control-panel mg-control-panel--secondary mg-control-panel--secondary-options w-full max-w-sm mg-filter mg-filter--has-hover-medium fixed top-0 bottom-0 right-0 text-white z-10"
        >
            <div
                class="overflow-y-auto p-4 absolute top-0 bottom-0 w-full mt-10"
            >
                <transition name="page-fade" mode="out-in">
                    <div v-if="'devices' === settingsPage">
                        <!-- TODO: use MgSelect.vue instead -->
                        <device-select
                            v-for="(device, index) in devices"
                            :device="device.name"
                            :label="device.label"
                            :select-options="device.options"
                            class="mb-6"
                            :key="`${device.name}-${index}`"
                        />
                    </div>
                    <nav v-else>
                        <ul
                            class="plain-buttons buttons-have-icon space-y-2 font-bold mb-4"
                        >
                            <li v-if="meetingHasConnection">
                                <button
                                    @click.prevent="goToSettingsPage('devices')"
                                >
                                    <i
                                        class="fa fa-plug mr-4"
                                        aria-hidden="true"
                                    ></i>
                                    <span class="leading-none">Devices</span>
                                </button>
                            </li>
                            <li>
                                <button @click.prevent="toggleFullScreen">
                                    <svg-shrink
                                        v-if="isFullScreen"
                                        class="mr-4"
                                        skey="icon-shrink"
                                    />
                                    <svg-enlarge
                                        v-else
                                        class="mr-4"
                                        skey="icon-enlarge"
                                    />

                                    <span
                                        v-if="isFullScreen"
                                        class="leading-none"
                                        key="exitfullscreenlabel"
                                    >
                                        Exit fullscreen
                                    </span>
                                    <span
                                        v-else
                                        class="leading-none"
                                        key="fullscreenlabel"
                                    >
                                        View fullscreen
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </transition>
            </div>

            <div
                class="h-10 absolute top-0 left-0 w-full border-b border-dividers flex justify-between items-center flex-row-reverse"
            >
                <button
                    @click="
                        toggleUiElement('mg-control-panel--secondary-options')
                    "
                    class="bg-transparent border-0 text-white p-0 mr-4"
                >
                    <i class="fa fa-times mr-2" aria-hidden="true"></i>
                    <span>Close</span>
                </button>
                <button
                    v-if="settingsPage"
                    @click="goToSettingsPage(null)"
                    class="bg-transparent border-0 text-white ui-navigation-control p-0 ml-4"
                >
                    <i
                        class="fa fa-angle-left mr-2 pointer-events-none"
                        aria-hidden="true"
                    ></i>
                    <span class="pointer-events-none">Back</span>
                </button>
            </div>
        </div>

        <div
            :class="{
                active: activeUiElements.includes('mg-control-panel--tertiary')
            }"
            class="mg-control-panel mg-control-panel--tertiary w-full max-w-sm mg-filter mg-filter--heavy fixed top-0 bottom-0 left-0 text-white z-10"
        >
            <div
                class="h-10 absolute top-0 left-0 w-full border-b border-dividers flex justify-between items-center flex-row-reverse"
            >
                <button
                    @click="toggleUiElement('mg-control-panel--tertiary')"
                    class="bg-transparent border-0 text-white p-0 mr-4"
                >
                    <i class="fa fa-times mr-2" aria-hidden="true"></i>
                    <span>Close</span>
                </button>

                <div class="w-1/2 mr-2">
                    <button
                        v-if="secondarySettingsPage"
                        @click="goToSecondarySettingsPage(null)"
                        class="bg-transparent border-0 text-white ui-navigation-control p-0 ml-4"
                    >
                        <i
                            class="fa fa-angle-left mr-2 pointer-events-none"
                            aria-hidden="true"
                        ></i>
                        <span class="pointer-events-none">Back</span>
                    </button>
                </div>
            </div>

            <div
                v-if="'chat' === secondarySettingsPage"
                class="top-0 bottom-0 w-full absolute mt-10"
            >
                <div
                    ref="chat-scroll"
                    class="fix-scroll-to-bottom overflow-y-auto p-4 absolute top-0 bottom-0 w-full mb-20"
                >
                    <message-list
                        dim-meta
                        disable-name-link
                        :message-data="messages"
                        class="text-white"
                    />

                    <div class="fix-scroll-to-bottom__anchor"></div>
                </div>

                <form
                    @submit.prevent="sendChatMessage"
                    class="h-20 absolute bottom-0 left-0 w-full"
                >
                    <div
                        class="absolute top-0 left-0 w-full h-full flex items-center"
                    >
                        <mg-select
                            id="chat-select"
                            defaultSelectOptionLabel="everyone"
                            class="text-xs absolute top-0 left-0 mt-2 ml-4 hidden"
                            label="Chat Select"
                            hide-label
                            slim
                            :options="chatSelectOptions"
                        ></mg-select>

                        <input
                            v-model="message"
                            class="appearance-none bg-transparent border-dividers border-t w-full h-full pt-5 px-2 pl-4 leading-tight outline-none text-white focus:border-accent placeholder-dividers"
                            type="text"
                            placeholder="enter chat messsage"
                            aria-label="Enter chat messsage"
                        />
                    </div>
                    <button class="sr-only">Submit</button>
                </form>
            </div>
            <div
                v-else-if="'promotions' === secondarySettingsPage"
                class="overflow-y-auto p-4 absolute top-0 bottom-0 w-full mt-10"
            >
                <transition-group
                    v-if="promotionQueue.length"
                    name="feed-transition"
                    tag="div"
                    enter-active-class="animated fadeInUp"
                    leave-active-class="animated fadeOut"
                    class="divide-y divide-dividers w-full h-64 -mt-3"
                >
                    <div
                        v-for="(item, index) in promotionQueue"
                        class="py-3 md:flex items-center"
                        :key="`${index}-${item.externalUserId}`"
                    >
                        <div class="md:w-1/2">
                            <div class="font-bold">
                                {{ formatName(item.info) }}
                            </div>
                            <div
                                v-if="item.info && item.info.companyName"
                                class="text-sm opacity-75 truncate"
                            >
                                {{ item.info.companyName }}
                            </div>
                        </div>

                        <div
                            v-if="item.chimeUserId"
                            class="space-x-2 flex justify-end md:w-1/2"
                        >
                            <button
                                v-if="isAttendeePromoted(item.chimeUserId)"
                                key="promote-attendee"
                                @click.prevent="
                                    requestDemoteAttendee(item.chimeUserId)
                                "
                                class="button is-danger is-small"
                            >
                                Demote
                            </button>
                            <button
                                v-else
                                key="demote-attendee"
                                @click.prevent="
                                    requestPromotion(item.chimeUserId)
                                "
                                class="button is-success is-outlined is-small"
                            >
                                <span class="font-bold">
                                    Promote
                                </span>
                            </button>
                        </div>
                    </div>
                </transition-group>
                <div v-else key="no-promos-requested">
                    No promotion requests.
                </div>
            </div>
            <nav
                v-else
                class="overflow-y-auto p-4 absolute top-0 bottom-0 w-full mt-10"
            >
                <ul
                    class="plain-buttons buttons-have-icon space-y-2 font-bold mb-4"
                >
                    <li v-if="meetingHasConnection">
                        <button
                            class="ui-navigation-control"
                            @click.prevent="goToSecondarySettingsPage('chat')"
                        >
                            <i
                                class="fa fa-comment mr-4 pointer-events-none"
                                aria-hidden="true"
                            ></i>
                            <span class="leading-none pointer-events-none"
                                >Chat</span
                            >
                        </button>
                    </li>
                    <li
                        v-if="
                            meetingHasConnection &&
                                isPresentation &&
                                isMeetingPresenter
                        "
                    >
                        <button
                            class="ui-navigation-control"
                            @click.prevent="
                                goToSecondarySettingsPage('promotions')
                            "
                        >
                            <i
                                class="fa fa-users-cog mr-4 pointer-events-none"
                                aria-hidden="true"
                            ></i>
                            <span class="leading-none pointer-events-none"
                                >Promotion requests</span
                            >
                        </button>
                    </li>
                </ul>
            </nav>
        </div>

        <div
            ref="mg-control-panel--primary"
            class="mg-control-panel mg-control-panel--primary w-screen fixed bottom-0 flex flex-wrap items-center justify-between mg-filter mg-filter--has-hover-medium z-50 overflow-visible"
        >
            <!-- control group 1 -->
            <div
                class="p-4 flex flex-wrap items-center flex-1 md:justify-start"
            >
                <b-tooltip
                    v-if="
                        meetingHasConnection &&
                            isPresentation &&
                            isMeetingPresenter
                    "
                    :label="
                        isPromotionsPageActive
                            ? 'Hide Promotion requests'
                            : 'Show Promotion requests'
                    "
                    type="is-dark"
                    :delay="500"
                    position="is-right"
                >
                    <button
                        @click="handleTertiaryPanel('promotions')"
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2 relative"
                    >
                        <i class="fa fa-users-cog m-0" aria-hidden="true"></i>
                        <div class="sr-only">Promotion requests</div>

                        <span
                            v-if="unreadPromotionsCount"
                            class="inline-block bg-accent text-white text-xs px-1 rounded-full uppercase font-semibold tracking-wide absolute bottom-0 right-0 translate-x-2 translate-y-1 transform"
                            >{{ unreadPromotionsCount }}</span
                        >
                    </button>
                </b-tooltip>

                <b-tooltip
                    v-if="meetingHasConnection"
                    :label="isChatActive ? 'Hide Chat' : 'Show Chat'"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        @click="handleTertiaryPanel('chat')"
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2 relative"
                    >
                        <i class="fa fa-comment m-0" aria-hidden="true"></i>
                        <div class="sr-only">Chat</div>

                        <span
                            v-if="unreadMessagesCount"
                            class="inline-block bg-accent text-white text-xs px-1 rounded-full uppercase font-semibold tracking-wide absolute bottom-0 right-0 translate-x-2 translate-y-1 transform"
                            >{{ unreadMessagesCount }}</span
                        >
                    </button>
                </b-tooltip>

                <b-tooltip
                    v-if="meetingHasConnection && allowScreenShare"
                    :label="
                        myContentIsSharing
                            ? 'Stop sharing'
                            : 'Share your screen'
                    "
                    :type="myContentIsSharing ? 'is-danger' : 'is-dark'"
                    :always="myContentIsSharing"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        v-if="hasScreenCapture"
                        @click="toggleContentShare()"
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2"
                    >
                        <i class="fa fa-desktop" aria-hidden="true"></i>
                        <div class="sr-only">Share screen</div>
                    </button>
                </b-tooltip>

                <b-tooltip
                    v-if="
                        meetingHasConnection &&
                            isPresentation &&
                            !isMeetingPresenter
                    "
                    :label="handIsRaised ? 'Lower hand' : 'Raise hand'"
                    :type="handIsRaised ? 'is-danger' : 'is-dark'"
                    :always="handIsRaised"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        @click.prevent="toggleRaiseHand"
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2 leading-none"
                    >
                        <svg-hand aria-hidden="true" />
                        <div class="sr-only">Ask to speak</div>
                    </button>
                </b-tooltip>
            </div>

            <!-- control group 2 -->
            <div class="p-4 flex flex-1 justify-center">
                <b-tooltip
                    v-if="
                        meetingHasConnection &&
                            currentAudioInput &&
                            (canAddAudio || !muted)
                    "
                    :label="muted ? 'Un-mute' : 'Mute'"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                    key="audioEnabled"
                >
                    <button
                        @click.prevent="setMuted"
                        class="ui-navigation-control bg-white h-12 w-12 text-xl rounded-full mx-4 border-0 flex items-center justify-center p-0"
                    >
                        <svg-mic-off
                            v-if="muted"
                            aria-hidden="true"
                            class="pointer-events-none"
                        />
                        <svg-mic
                            v-else
                            aria-hidden="true"
                            class="pointer-events-none"
                        />

                        <span
                            v-if="muted"
                            class="sr-only pointer-events-none"
                            key="muted"
                            >Unmute</span
                        >
                        <span
                            v-else
                            class="sr-only pointer-events-none"
                            key="unmuted"
                            >Mute</span
                        >
                    </button>
                </b-tooltip>
                <b-tooltip
                    v-else-if="meetingHasConnection"
                    :label="disabledMicrophoneLabel"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                    key="audioNotEnabled"
                >
                    <button
                        disabled
                        class="bg-white h-12 w-12 text-xl rounded-full mx-4 border-0 flex items-center justify-center p-0"
                        type="button"
                    >
                        <svg-mic-off aria-hidden="true" />
                        <span class="sr-only">
                            {{ disabledMicrophoneLabel }}
                        </span>
                    </button>
                </b-tooltip>

                <b-tooltip
                    label="Leave meeting"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        @click.prevent="leaveMeeting"
                        class="bg-red-700 border-0 text-white h-12 w-12 text-xl rounded-full mx-4 flex items-center justify-center p-0"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                        <div class="sr-only">Leave meeting</div>
                    </button>
                </b-tooltip>

                <b-tooltip
                    v-if="
                        meetingHasConnection &&
                            currentVideoDevice &&
                            (canAddVideo || cameraOn)
                    "
                    :label="cameraOn ? 'Disable camera' : 'Enable camera'"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        class="ui-navigation-control bg-white h-12 w-12 text-xl rounded-full mx-4 border-0 flex items-center justify-center p-0"
                        @click="toggleVideo()"
                    >
                        <svg-camera
                            v-if="cameraOn"
                            aria-hidden="true"
                            class="pointer-events-none"
                        />
                        <svg-camera-slash
                            v-else
                            aria-hidden="true"
                            class="pointer-events-none"
                        />

                        <span
                            v-if="cameraOn"
                            class="sr-only pointer-events-none"
                            key="cameraOn"
                            >Disable camera</span
                        >
                        <span
                            v-else
                            class="sr-only pointer-events-none"
                            key="cameraOff"
                            >Enable camera</span
                        >
                    </button>
                </b-tooltip>

                <b-tooltip
                    v-else-if="meetingHasConnection"
                    :label="disabledCameraLabel"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        disabled
                        class="bg-white h-12 w-12 text-xl rounded-full mx-4 border-0 flex items-center justify-center p-0"
                        type="button"
                    >
                        <svg-camera-slash aria-hidden="true" />
                        <span class="sr-only" key="cameraOff">
                            {{ disabledCameraLabel }}
                        </span>
                    </button>
                </b-tooltip>
            </div>

            <!-- control group 3 -->
            <div class="p-4 flex flex-1 md:justify-end">
                <div v-if="isPresentation" class="relative">
                    <transition
                        tag="div"
                        enter-active-class="animated fadeInUp"
                        leave-active-class="animated fadeOut"
                    >
                        <div
                            v-if="activeUiElements.includes('attendee-list')"
                            class="absolute top-0 -mt-4 left-half bg-accent w-0"
                        >
                            <div
                                class="transform -translate-y-full sm:-translate-x-1/2 w-56"
                            >
                                <div
                                    class="overflow-hidden bg-white shadow rounded w-full transform"
                                >
                                    <div class="px-4 py-2 overflow-y-auto">
                                        <p
                                            class="text-sm font-bold opacity-50 m-0"
                                        >
                                            Attendees
                                        </p>
                                        <div
                                            class="divide-y divide-dividers w-full h-64"
                                        >
                                            <div
                                                v-for="(value,
                                                key,
                                                index) in attendeeListing"
                                                class="py-2"
                                                :key="index"
                                            >
                                                {{ value.info.displayName }}
                                                <div
                                                    v-if="
                                                        value.info.companyName
                                                    "
                                                    class="text-sm opacity-50 truncate"
                                                >
                                                    {{ value.info.companyName }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    @click="toggleUiElement('attendee-list')"
                                    class="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 border-0 bg-white h-8 w-8 rounded-full shadow p-0 flex items-center justify-center"
                                >
                                    <i
                                        class="fa fa-times"
                                        aria-hidden="true"
                                    ></i>
                                    <span class="sr-only">Close attendees</span>
                                </button>
                            </div>
                        </div>
                    </transition>
                    <b-tooltip
                        v-if="meetingHasConnection"
                        label="Toggle attendee list"
                        type="is-dark"
                        :delay="500"
                        position="is-left"
                    >
                        <button
                            v-if="!$data.contentTile"
                            @click="toggleUiElement('attendee-list')"
                            class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2"
                        >
                            <i class="fa fa-users" aria-hidden="true"></i>
                            <div class="sr-only">Toggle attendee list</div>
                        </button>
                    </b-tooltip>
                </div>

                <b-tooltip
                    v-if="meetingHasConnection"
                    label="Toggle grid view"
                    type="is-dark"
                    :delay="500"
                    position="is-top"
                >
                    <button
                        v-if="!$data.contentTile"
                        @click.prevent="toggleGridView"
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full mx-2"
                    >
                        <i class="fa fa-th-large" aria-hidden="true"></i>
                        <div class="sr-only">Toggle grid view</div>
                    </button>
                </b-tooltip>
                <b-tooltip
                    label="Settings"
                    type="is-dark"
                    :delay="500"
                    position="is-left"
                >
                    <button
                        @click="
                            toggleUiElement(
                                'mg-control-panel--secondary-options'
                            )
                        "
                        class="bg-transparent border-0 text-white text-xl xl:text-2xl rounded-full ml-2 mr-4"
                    >
                        <i class="fa fa-cog" aria-hidden="true"></i>
                        <div class="sr-only">Settings</div>
                    </button>
                </b-tooltip>
            </div>
        </div>

        <transition-group
            tag="div"
            class="tracking-wide fixed bottom-0 left-0 mb-4 ml-4 space-x-2 flex"
            enter-active-class="animated fadeInUp"
            leave-active-class="animated fadeOut"
        >
            <div
                v-if="
                    panelsAreClosed &&
                        unreadPromotionsCount &&
                        isPresentation &&
                        isMeetingPresenter
                "
                key="unread-attendee-options"
                class="text-white text-xs py-1 px-3 rounded-full mg-filter z-10"
            >
                <i
                    class="fa fa-users-cog mr-2 inline-block"
                    aria-hidden="true"
                ></i>
                <span aria-hidden="true">{{ unreadPromotionsCount }}</span>
                <span role="alert" class="sr-only"
                    >There are some new attendee options</span
                >
            </div>

            <div
                key="unread-comments"
                v-if="panelsAreClosed && unreadMessagesCount"
                class="text-white text-xs py-1 px-3 rounded-full mg-filter z-10"
            >
                <i
                    class="fa fa-comment mr-2 inline-block"
                    aria-hidden="true"
                ></i>
                <span aria-hidden="true">{{ unreadMessagesCount }}</span>
                <span role="alert" class="sr-only"
                    >There are some unread chat messages</span
                >
            </div>
        </transition-group>

        <!-- this needs to be last child element -->
        <div
            v-if="isLoading"
            key="loading-data"
            class="fixed z-50 top-0 left-0 w-screen h-screen bg-white flex items-center"
        >
            <spinners />
        </div>
    </div>
</template>

<script>
import apiSvc from "@/services/api";
import linkifyHtml from "linkifyjs/html";
import {
    AsyncScheduler,
    ConsoleLogger,
    DataMessage,
    DefaultActiveSpeakerPolicy,
    DefaultDeviceController,
    DefaultMeetingSession,
    DefaultModality,
    LogLevel,
    MeetingSessionConfiguration
} from "amazon-chime-sdk-js";
import { mapState, mapGetters, mapActions } from "vuex";
import { differenceInSeconds } from "date-fns";
import { getModule } from "vuex-module-decorators";
import attendeeVuexModule from "@/store/vuex-modules/attendees";

//import { v4 } from 'uuid';
import messageList from "@/components/MessageList";
import mgSelect from "@/components/shared/MgSelect";
import deviceSelect from "@/components/shared/device-select";
import svgHand from "@/components/svg/svg-hand";
import svgCamera from "@/components/svg/svg-camera";
import svgCameraSlash from "@/components/svg/svg-camera-slash";
import svgMic from "@/components/svg/svg-mic";
import svgMicOff from "@/components/svg/svg-mic-off";
import svgShrink from "@/components/svg/svg-shrink";
import svgEnlarge from "@/components/svg/svg-enlarge";
import svgConnection from "@/components/svg/svg-connection";
import svgConnection2 from "@/components/svg/svg-connection-2";
import svgConnection3 from "@/components/svg/svg-connection-3";
import svgConnection4 from "@/components/svg/svg-connection-4";
import svgVolumeHigh from "@/components/svg/svg-volume-high";
import svgVolumeMedium from "@/components/svg/svg-volume-medium";
import svgVolumeLow from "@/components/svg/svg-volume-low";
import svgVolumeMute from "@/components/svg/svg-volume-mute";
import eventHub from "@/event-hub";
import spinners from "@/components/utilities/Spinners.vue";

const attendeeStore = getModule(attendeeVuexModule);
const ONE_MINUTE = 60;
const DEFAULT_MEETING_DURATION = 240 * ONE_MINUTE;
const DEFAULT_EXTRA_MINUTES = 10;

//const uuid = v4;
// Todo: rename this file MeetingView.vue
// https://vuejs.org/v2/style-guide/#Multi-word-component-names-essential
export default {
    name: "MeetingView",
    components: {
        svgHand,
        svgCamera,
        svgCameraSlash,
        svgMic,
        svgMicOff,
        svgShrink,
        svgEnlarge,
        mgSelect,
        deviceSelect,
        messageList,
        spinners,
        svgConnection,
        svgConnection2,
        svgConnection3,
        svgConnection4,
        svgVolumeHigh,
        svgVolumeMedium,
        svgVolumeLow,
        svgVolumeMute
    },
    directives: {
        addvideoel: {
            update: function(el, binding, vnode) {
                const id = el.getAttribute("data-attendee-id");
                let video = null;
                if (!id) return;

                if (vnode.context.roster[id] === undefined) {
                    console.log("Bad in addvideoel");
                }
                video = vnode.context.roster[id].video;

                if (
                    !video ||
                    (!vnode.context.gridViewIsActive &&
                        video == vnode.context.currentSpeakerEl)
                ) {
                    video = vnode.context.getStaticElement(id);
                }
                if (video && video instanceof HTMLElement) {
                    video.className =
                        "h-full w-full top-0 left-0 absolute object-cover bg-black z-0";
                    el.appendChild(video);
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(_ => {
                                /* do nothing */
                            })
                            .catch(error => {
                                /* do nothing */
                            });
                    }
                }
            }
        },
        addspeakerel: {
            update: function(el, binding, vnode) {
                const speakerNode = vnode.context.currentSpeakerEl;
                if (!speakerNode) return;

                if (speakerNode instanceof HTMLElement) {
                    el.innerHTML = "";
                    el.appendChild(speakerNode);
                    const playPromise = speakerNode.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(_ => {
                                /* do nothing */
                            })
                            .catch(error => {
                                /* do nothing */
                            });
                    }
                }
            }
        }
    },
    data() {
        window.meetingstuff = this;
        return {
            meetingTitle: "",
            brandLogo: "",
            initialBrandColor: "",
            meetingHasTopic: false,
            meetingHasConnection: false,
            secondsCountDown: 0,
            creationTime: null,
            meetingApiData: null,
            isLoading: true,
            hasScreenCapture: false,
            boundAudioElement: false,
            chatSelectOptions: [],
            chatSelection: "",
            currentSpeakerInterval: null,
            gridViewIsActive: true,
            chime: {
                meetingSession: null
            },
            uiWrapper: null,
            uiObserver: null,
            meetingCount: null,
            maxVideoFeeds: 12,
            meetingSecondsInitial: 0,
            meetingMinutesDisplay: "00",
            meetingSecondsDisplay: "00",
            extraMinutes: DEFAULT_EXTRA_MINUTES,
            extraMinutesActive: false,
            timeRemaining: 0,
            meetingIsOver: false,
            unknownName: "Loading...",
            settingsPage: "",
            secondarySettingsPage: "",
            message: "",
            currentSpeaker: undefined,
            activeUiElements: [],
            videoFeeds: {},
            attendeeInfo: {},
            isPromoted: false,
            thumbnailSpeakers: [],
            videoAttendees: [], // Attendees who are active on video
            audioAttendees: [], // Attendees who are not currently active on video
            handIsRaised: false,
            raisedHands: [],
            raisedHandsAtPanelClosed: [],
            promotedFolks: [],
            vaAttendees: [], // Attendees who are currently voice or audio (non-silent)
            silentAttendees: [], // Observers in presentation mode
            idleTimer: null,
            idleState: false,
            idleWait: 1500,
            speakerInitialized: true,
            meetingId: "",
            grabCursor: true,
            audioInputs: [],
            audioOutputs: [],
            videoInputs: [],
            canStartLocalVideo: false,
            remoteVideoAvailable: false,
            roster: {},
            rosterVideos: {},
            messageTopics: [
                "chat",
                "raisehand",
                "lowerhand",
                "promote",
                "demote"
            ],
            DATA_MESSAGE_LIFETIME_MS: 600000,
            lastReceivedMessageTimestamp: 0,
            cameraOn: false,
            cameraOffSelect: true,
            muted: false,
            myContentIsSharing: false,
            canAddVideo: false,
            canAddAudio: false,
            currentAudioInput: "",
            currentAudioOutput: "",
            currentVideoDevice: "",
            contentTile: null,
            messages: [],
            messagesAtPanelClosed: 0,
            swiperOption: {
                mousewheel: true,
                direction: "vertical",
                slidesPerView: "auto",
                slidesPerGroup: 3,
                spaceBetween: 30,
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev"
                }
            }
        };
    },
    computed: {
        ...mapState(["user", "isFullScreen"]),
        ...mapGetters([
            "speakeasyOptions",
            "logoMeetingView",
            "conferenceName",
            "getNavItem"
        ]),
        extraSeconds() {
            return this.extraMinutes * ONE_MINUTE;
        },
        extraSecondsOvertime() {
            // Subtract an additional 2 seconds
            // the additional 2 seconds is for the time in which the countdown display is reset
            return this.extraSeconds * -1 - 2;
        },

        currentSpeakerData() {
            let returnValue = null;

            if (this.currentSpeaker && this.roster[this.currentSpeaker]) {
                returnValue = this.roster[this.currentSpeaker];
            }

            return returnValue;
        },

        promotionQueue() {
            const source = this.raisedHands;
            const returnValue = [];

            source.map(item => {
                const isSelf = Boolean(this.getSelfAttendeeId() === item);
                const updateItem = this.roster[item];

                if (!isSelf && updateItem) {
                    updateItem.chimeUserId = item;
                    returnValue.push(updateItem);
                }
            });

            return returnValue;
        },

        disabledMicrophoneLabel() {
            let returnValue = "Audio at capacity";

            if (!this.currentAudioInput) {
                returnValue = "No audio input selected";
            } else if (this.isPresentation) {
                returnValue = "Microphone not allowed";
            }

            return returnValue;
        },
        disabledCameraLabel() {
            let returnValue = "Video at capacity";

            if (!this.currentVideoDevice) {
                returnValue = "No video input selected";
            } else if (this.isPresentation) {
                returnValue = "Camera not allowed";
            }

            return returnValue;
        },
        uniqueAttendees() {
            const returnValue = [];
            const attendeeIds = [];

            for (const [key, value] of Object.entries(this.roster)) {
                const attendeeId = value?.info?.attendeeId;
                if (attendeeId && !attendeeIds.includes(attendeeId)) {
                    returnValue.push(key);
                    attendeeIds.push(attendeeId);
                }
            }

            return returnValue;
        },
        attendeeListing() {
            const source = this.uniqueAttendees;
            const returnValue = {};

            source.map(item => {
                if (this.roster[item]) {
                    returnValue[item] = this.roster[item];
                }
            });

            return returnValue;
        },
        attendeesForDisplay() {
            const source = this.vaAttendees;
            const returnValue = {};

            source.map(item => {
                if (this.roster[item]) {
                    returnValue[item] = this.roster[item];
                }
            });

            return returnValue;
        },
        queriedAttendee: {
            get() {
                return attendeeStore.attendee;
            },
            set() {
                // n/a
            }
        },
        meetingDuration() {
            let returnValue = 0;
            let startTime = null;
            let endTime = null;

            if (
                this.meetingData &&
                this.meetingData.startTimeISO &&
                this.meetingData.endTimeISO
            ) {
                startTime = new Date(this.meetingData.startTimeISO);
                endTime = new Date(this.meetingData.endTimeISO);

                returnValue = differenceInSeconds(endTime, startTime);
            } else {
                returnValue = DEFAULT_MEETING_DURATION;
            }

            return returnValue;
        },
        isUiActive() {
            return Boolean(
                !this.idleState ||
                    this.activeUiElements.length ||
                    this.extraMinutesActive
            );
        },
        isTertiaryPanelActive() {
            return Boolean(
                this.activeUiElements.includes("mg-control-panel--tertiary")
            );
        },
        panelsAreClosed() {
            return Boolean(
                0 === this.activeUiElements.length && this.idleState
            );
        },
        isChatActive() {
            const isChatOpen = Boolean(
                "chat" === this.secondarySettingsPage &&
                    this.isTertiaryPanelActive
            );

            return isChatOpen;
        },
        isPromotionsPageActive() {
            const returnValue = Boolean(
                "promotions" === this.secondarySettingsPage &&
                    this.isTertiaryPanelActive
            );

            return returnValue;
        },
        unreadPromotionsCount() {
            const newItems = this.raisedHands.filter(item => {
                if (!this.isPromotionsPageActive) {
                    return !this.raisedHandsAtPanelClosed.includes(item);
                } else {
                    return false;
                }
            });

            if (!this.isPromotionsPageActive) {
                return newItems.length;
            } else {
                return 0;
            }
        },
        unreadMessagesCount() {
            const unread = this.messages.length - this.messagesAtPanelClosed;

            if (null === this.messagesAtPanelClosed) {
                return 0;
            } else if (unread > 0) {
                return unread;
            } else {
                return 0;
            }
        },
        hasVideoThumbnails() {
            return this.thumbnailSpeakers.length;
        },
        devices() {
            const deviceArray = [
                {
                    name: "audio_input",
                    label: "Microphones",
                    options: this.audioInputs
                },
                {
                    name: "audio_output",
                    label: "Speakers",
                    options: this.audioOutputs
                },
                {
                    name: "video_input",
                    label: "Camera",
                    options: this.videoInputs
                }
            ];

            return deviceArray;
        },
        meetingData() {
            let returnValue = null;
            if (this.meetingApiData && this.meetingApiData.meetingData) {
                returnValue = this.meetingApiData.meetingData;
            }
            return returnValue;
        },
        isPresentation() {
            return Boolean("presentation" === this.getMeetingData("type"));
        },
        meetingAttendeeData() {
            const data = this.meetingApiData.attendeeData;
            return data;
        },
        isMeetingPresenter() {
            return Boolean(
                this.meetingAttendeeData && this.meetingAttendeeData.isPresenter
            );
        },
        allowScreenShare() {
            const disableMeetingTypes = ["presentation"];
            const isDisableType = disableMeetingTypes.includes(
                this.getMeetingData("type")
            );
            let returnValue = true;

            if (isDisableType) {
                if (this.isPresentation) {
                    returnValue = Boolean(
                        this.isPresentation && this.isMeetingPresenter
                    );
                }
            }

            return returnValue;
        }
    },
    watch: {
        isPromoted() {
            this.handleControlPermissions();
        },
        roster: {
            handler(val, oldVal) {
                const options = [];

                Object.keys(this.roster).map(key => {
                    const attendee = this.roster[key];

                    if (attendee && attendee.info) {
                        const option = {
                            value: attendee.info.attendeeId,
                            label: attendee.info.displayName
                        };
                        options.push(option);
                    }
                });

                this.chatSelectOptions = options;
            },
            deep: true
        },
        isChatActive() {
            if (this.isChatActive) {
                this.messagesAtPanelClosed = null;
            } else {
                this.messagesAtPanelClosed = this.messages.length;
            }
        },
        isPromotionsPageActive() {
            if (this.isPromotionsPageActive) {
                this.raisedHandsAtPanelClosed = [];
            } else {
                const raisedHandsSnapshot = JSON.parse(
                    JSON.stringify(this.raisedHands)
                );
                this.raisedHandsAtPanelClosed = raisedHandsSnapshot;
            }
        },
        meetingIsOver() {
            // kick everyone out
            if (this.meetingIsOver) {
                this.leaveMeeting();
            }
        }
    },
    created() {
        this.creationTime = this.MgRightNow();
        this.loadSavedPreferences();
        this.handleConfigOptions();

        // TODO
        // set up use a vuex module to contain all feature detects,
        // best to use vuex module in case we need these elsewhere.
        this.handleFeatureDetection();

        window.addEventListener("mousemove", this.handleIdleCount);
        window.addEventListener("click", this.handleIdleCount);
        window.addEventListener("keydown", this.handleIdleCount);
        window.addEventListener("touchstart", this.handleIdleCount);

        eventHub.$on("key-pressed-escape", this.clearActiveUi);
        eventHub.$on("select-selection", this.handleChatSelect);

        eventHub.$on("device-selection", this.handleDeviceSelect);
    },

    mounted() {
        // Init depends on DOM elements

        this.init()
            .then(() => {
                if (this.meetingHasConnection) {
                    this.handleMeetingTime();
                    this.handleLogUsage();
                }
            })
            .catch(() => {
                console.error("Init failed.");
            })
            .finally(() => {
                this.handleMeetingPreferences();
                this.isLoading = false;
            });

        this.uiWrapper = this.$refs["mg-control-panel-wrapper"];

        if (this.uiWrapper) {
            this.uiWrapper.addEventListener("click", this.handleUiCancelLayer);
            this.uiWrapper.addEventListener(
                "touchstart",
                this.handleUiCancelLayer
            );

            if ("ResizeObserver" in window) {
                this.uiObserver = new ResizeObserver(this.handleUiDefinitions);
                this.uiObserver.observe(this.uiWrapper);
            } else {
                this.handleUiDefinitions();
            }
        }
    },

    // It's good to clean up event listeners before
    // a component is destroyed.
    beforeDestroy() {
        clearInterval(this.meetingCount);
        clearInterval(this.currentSpeakerInterval);
        clearInterval(this.logInterval);

        if (this.uiWrapper) {
            this.uiWrapper.removeEventListener(
                "click",
                this.handleUiCancelLayer
            );
            this.uiWrapper.removeEventListener(
                "touchstart",
                this.handleUiCancelLayer
            );

            if ("ResizeObserver" in window) {
                this.uiObserver.unobserve(this.uiWrapper);
            }
        }

        window.removeEventListener("mousemove", this.handleIdleCount);
        window.removeEventListener("click", this.handleIdleCount);
        window.removeEventListener("keydown", this.handleIdleCount);
        window.removeEventListener("touchstart", this.handleIdleCount);

        eventHub.$off("key-pressed-escape", this.clearActiveUi);
        eventHub.$off("select-selection", this.handleChatSelect);

        eventHub.$off("device-selection", this.handleDeviceSelect);

        this.cleanupChime();
    },

    methods: {
        ...mapActions(["toggleFullScreen"]),
        loadSavedPreferences() {
            this.currentAudioInput = localStorage.getItem(
                "preferredAudioInput"
            );
            this.currentAudioOutput = localStorage.getItem(
                "preferredAudioOutput"
            );
            this.currentVideoDevice = localStorage.getItem(
                "preferredVideoInput"
            );
        },
        handleMeetingPreferences() {
            this.loadLogo();
            this.setColors();
            this.setMeetingTitle();
        },
        loadLogo() {
            if (this.getMeetingData("meetingLogo")) {
                this.brandLogo = this.getMeetingData("meetingLogo");
            } else {
                this.brandLogo = this.logoMeetingView;
            }
        },
        setColors() {
            const documentElement = document.documentElement;
            let brandColor = "";

            if (this.getMeetingData("meetingColor")) {
                brandColor = this.meetingData.meetingColor;
            } else if (this.initialBrandColor) {
                brandColor = this.initialBrandColor;
            }

            if (brandColor) {
                documentElement.style.setProperty("--brand", brandColor);
            }
        },
        setMeetingTitle() {
            const topic = this.getMeetingData("displayTopic");

            if (topic) {
                this.meetingTitle = topic;
                this.meetingHasTopic = true;
            } else if (this.conferenceName) {
                this.meetingTitle = this.conferenceName;
            }
        },
        getMeetingData(property = "") {
            let returnValue = "";
            if (this.meetingData && this.meetingData[property]) {
                returnValue = this.meetingData[property];
            }
            return returnValue;
        },
        handleConfigOptions() {
            if (!this.speakeasyOptions) return;

            const extraMinutesOption = parseInt(
                this.speakeasyOptions?.extraMinutes
            );
            if (-1 < extraMinutesOption) {
                this.extraMinutes = extraMinutesOption;
            }
        },
        setInitialBrandColor() {
            const documentElement = document.documentElement;
            const primaryColor = documentElement.style.getPropertyValue(
                "--primary"
            );
            const brandColor = documentElement.style.getPropertyValue(
                "--brand"
            );
            if (brandColor) {
                this.initialBrandColor = brandColor;
            } else if (primaryColor) {
                this.initialBrandColor = primaryColor;
            } else {
                this.initialBrandColor = "#222";
            }
        },
        logKickedOutEvent() {
            this.$rollbar.info(
                `Kicked out of Meeting: ${
                    this.$route.params.sessionId
                }, user: ${this.user.id}, timeRemaining: ${
                    this.timeRemaining
                }, secondsCountDown: ${this.secondsCountDown}, creationTime: ${
                    this.creationTime
                }, serverTime: ${this.MgRightNow()}`
            );
        },
        getSelfAttendeeId() {
            // TODO: `selfAttendeeId` should be a computed property.
            // for now not using computed property becuase of reactivity issue.
            const returnValue = "";
            let myId = "";
            if (this.chime.meetingSession) {
                myId = this.chime.meetingSession?.configuration?.credentials
                    ?.attendeeId;
            }
            return myId || returnValue;
        },
        attendeeIsPresenter(attendeeId) {
            return (
                this.meetingData &&
                this.meetingData.presenters &&
                this.meetingData.presenters.indexOf(attendeeId) >= 0
            );
        },
        setMeetingTimeRemaining() {
            let returnValue = 0;
            let startTime = 0;
            let timeSinceStart = null;
            const currentTime = this.MgRightNow();

            if (this.meetingData && this.meetingData.startTimeISO) {
                startTime = new Date(this.meetingData.startTimeISO);
            } else {
                startTime = this.creationTime;
            }

            if (currentTime && startTime && 0 < this.meetingDuration) {
                timeSinceStart = differenceInSeconds(currentTime, startTime);

                if (timeSinceStart > -1) {
                    returnValue = this.meetingDuration - timeSinceStart;
                } else {
                    const earlySeconds = timeSinceStart * -1;
                    returnValue = earlySeconds + this.meetingDuration;
                }
            }

            this.timeRemaining = returnValue;
        },
        handleFeatureDetection() {
            this.hasScreenCapture = Boolean(
                (navigator.mediaDevices &&
                    navigator.mediaDevices.getDisplayMedia) ||
                    navigator.getDisplayMedia
            );
        },
        removeElement(array, elem) {
            while (array.indexOf(elem) > -1) {
                const index = array.indexOf(elem);
                if (index > -1) {
                    array.splice(index, 1);
                }
            }
            this.setVAAttendees();
        },
        setVAAttendees() {
            this.vaAttendees.splice(0, this.vaAttendees.length);
            for (let i = 0; i < this.videoAttendees.length; i++) {
                this.vaAttendees.push(this.videoAttendees[i]);
            }
            for (let i = 0; i < this.audioAttendees.length; i++) {
                this.vaAttendees.push(this.audioAttendees[i]);
            }
        },
        addVideoMember(id) {
            if (this.videoAttendees.indexOf(id) < 0) {
                this.videoAttendees.push(id);
            }
            this.removeElement(this.audioAttendees, id);
            this.removeElement(this.silentAttendees, id);
            this.setVAAttendees();
        },
        addAudioMember(id) {
            if (this.audioAttendees.indexOf(id) < 0) {
                this.audioAttendees.push(id);
            }
            this.removeElement(this.videoAttendees, id);
            this.removeElement(this.silentAttendees, id);
            this.setVAAttendees();
        },
        addSilentMember(id) {
            if (this.silentAttendees.indexOf(id) < 0) {
                this.silentAttendees.push(id);
            }
            this.removeElement(this.audioAttendees, id);
            this.removeElement(this.videoAttendees, id);
        },
        handleChatSelect(payload = null) {
            if (!payload || "chat-select" !== payload.id) return;
            this.chatSelection = payload.value;
        },
        toggleGridView() {
            this.gridViewIsActive = !this.gridViewIsActive;
        },
        handleUiCancelLayer(event) {
            const element = event.target;

            if (element === undefined) {
                console.log("bad handleUiCancelLayer");
            }

            if (
                !element.classList.contains("ui-navigation-control") &&
                !element.closest(".mg-control-panel")
            ) {
                this.clearActiveUi();
            }
        },
        handleUiDefinitions() {
            const primaryControls = this.$refs["mg-control-panel--primary"];
            let primaryControlsHeight = 80;

            if (primaryControls) {
                primaryControlsHeight = primaryControls.getBoundingClientRect()
                    .height;
            }

            if (this.uiWrapper) {
                this.uiWrapper.style.setProperty(
                    "--mg-control-panel--primary-height",
                    `${primaryControlsHeight}px`
                );
            }
        },
        clearActiveUi() {
            this.activeUiElements = [];
        },
        toggleContentShare() {
            if (!this.allowScreenShare) return;

            if (this.myContentIsSharing) {
                this.stopContentShare();
            } else if (this.contentTile) {
                //FIXME Cannot share while another person is sharing
                const keys = [];
                let p;
                for (p in this.videoFeeds)
                    if (
                        Object.prototype.hasOwnProperty.call(this.videoFeeds, p)
                    )
                        keys.push(p);
                for (let i = 0; i < keys.length; i++) {
                    const info = this.videoFeeds[keys[i]];
                    if (info.isContent) {
                        return;
                    }
                }
            } else {
                this.startContentShare();
            }
        },
        startContentShare() {
            const sharePromise = this.chime.meetingSession.audioVideo.startContentShareFromScreenCapture();
            if (sharePromise !== undefined) {
                sharePromise
                    .then(() => {
                        this.myContentIsSharing = true;
                    })
                    .catch(() => {
                        // this is called if the user hits cancel from the screen share dialog
                        this.myContentIsSharing = false;
                    });
            }
        },
        stopContentShare() {
            const stopSharingPromise = this.chime.meetingSession.audioVideo.stopContentShare();
            if (stopSharingPromise !== undefined) {
                stopSharingPromise
                    .then(() => {
                        this.myContentIsSharing = false;
                    })
                    .catch(error => {
                        console.error(error);
                    });
            } else {
                this.myContentIsSharing = false;
            }
        },
        toggleVideo: async function() {
            if (this.cameraOn) {
                try {
                    await this.chime.meetingSession.audioVideo.stopLocalVideoTile();
                    await this.chime.meetingSession.audioVideo.removeLocalVideoTile();
                } catch (error) {
                    console.error("Couldn't stop tile: ", error);
                }
                this.cameraOffSelect = true;
                this.cameraOn = false;
            } else {
                if (Object.keys(this.videoFeeds).length >= this.maxVideoFeeds) {
                    return;
                }
                this.cameraOn = true;
                this.cameraOffSelect = false;

                await this.setVideoInput(this.currentVideoDevice);

                try {
                    await this.chime.meetingSession.audioVideo.startLocalVideoTile();
                } catch (error) {
                    console.error("Couldn't start tile: ", error);
                }
            }
        },
        toggleUiElement(elementClass = "") {
            if (!elementClass) {
                return;
            }
            const myIndex = this.activeUiElements.indexOf(elementClass);

            if (-1 < myIndex) {
                // Hide element
                this.activeUiElements.splice(myIndex, 1);
            } else {
                // Show element
                this.activeUiElements.push(elementClass);
            }
        },
        handleIdleCount() {
            clearTimeout(this.idleTimer);

            if (this.activeUiElements.length) {
                //abort
                return;
            }

            this.idleState = false;

            this.idleTimer = setTimeout(() => {
                this.idleState = true;
            }, this.idleWait);
        },
        handleDeviceSelect(payload) {
            if ("audio_input" === payload.device) {
                this.setAudioInput(payload.value);
            } else if ("audio_output" === payload.device) {
                this.setAudioOutput(payload.value);
            } else if ("video_input" === payload.device) {
                this.setVideoInput(payload.value);
            }
        },
        setAudioInput: async function(value) {
            if (value) {
                this.currentAudioInput = value;
                localStorage["preferredAudioInput"] = value;
            }

            try {
                await this.chime.meetingSession.audioVideo.chooseAudioInputDevice(
                    value
                );
            } catch (error) {
                console.error("Could not set audio input: ", value, error);
                return;
            }
        },
        setAudioOutput: async function(value) {
            try {
                await this.chime.meetingSession.audioVideo.chooseAudioOutputDevice(
                    value
                );
            } catch (error) {
                console.error("Could not set audio output: ", value, error);
                return;
            }
            localStorage["preferredAudioOutput"] = value;
            const audioOutputElement = document.getElementById("GrahamBell");
            if (!this.boundAudioElement) {
                try {
                    await this.chime.meetingSession.audioVideo.bindAudioElement(
                        audioOutputElement
                    );
                } catch (error) {
                    console.error(
                        "Unable to bind audio element: ",
                        value,
                        error
                    );
                }
                this.boundAudioElement = true;
            }
        },
        setVideoInput: async function(value) {
            if (value) {
                this.currentVideoDevice = value;
                localStorage.setItem("preferredVideoInput", value);
            }

            if (value !== undefined) {
                if (!this.isPresenter) {
                    try {
                        await this.chime.meetingSession.audioVideo.chooseVideoInputQuality(
                            320,
                            180,
                            10,
                            600
                        );
                    } catch (error) {
                        console.error("WARNING: not setting resolution");
                    }
                }
                try {
                    await this.chime.meetingSession.audioVideo.chooseVideoInputDevice(
                        value
                    );
                } catch (error) {
                    console.error(
                        "Unable to select video input: ",
                        value,
                        error
                    );
                    return;
                }
            }
        },
        audioInputsChanged: function(freshInputs) {
            this.audioInputs = freshInputs;
        },
        audioOutputsChanged: function(freshOutputs) {
            this.audioOutputs = freshOutputs;
        },
        videoInputsChanged: function(freshInputs) {
            this.videoInputs = freshInputs;
        },
        setMuted: async function() {
            if (
                !this.chime.meetingSession &&
                !this.chime.meetingSession.audioVideo
            )
                return;

            this.muted = !this.muted;

            if (this.muted) {
                //FIXME and below
                await this.chime.meetingSession.audioVideo.realtimeMuteLocalAudio();
            } else {
                await this.chime.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
            }
        },
        activeSpeakerHandler(speakerInfo) {
            // Content shares override the active speaker...
            if (this.contentTile) {
                if (this.nodeToShow != this.contentTile) {
                    this.nodeToShow = this.contentTile.boundVideoElement;
                    //Switch to content share immediately
                    this.currentSpeakerEl = this.nodeToShow;
                }
                return;
            }

            // Default to self if nobody is active...
            if (this.myTile) {
                this.nodeToShow = this.myTile.boundVideoElement;
            }
            for (const attendeeId of speakerInfo) {
                if (this.myTile && attendeeId != this.myTile.boundAttendeeId) {
                    this.currentSpeaker = attendeeId;
                    break;
                }
            }

            if (
                this.roster[this.currentSpeaker] &&
                this.roster[this.currentSpeaker].video
            ) {
                const newElement = this.roster[this.currentSpeaker].video;
                if (newElement) {
                    this.nodeToShow = newElement;
                }
            }
            if (!this.currentSpeakerInterval) {
                this.currentSpeakerInterval = window.setInterval(() => {
                    this.currentSpeakerEl = this.nodeToShow;
                }, 1500);
            }
        },
        getAttendeeData(attendeeId, isSelf = false) {
            const options = { id: attendeeId, isDemoData: false };

            return new Promise((resolve, reject) => {
                if (!attendeeId) {
                    return reject("no attendeeId");
                }

                attendeeStore
                    .getAttendee(options)
                    .then(() => {
                        try {
                            if (undefined === this.queriedAttendee) {
                                return;
                            }
                            const item = JSON.parse(
                                JSON.stringify(this.queriedAttendee)
                            );
                            item["displayName"] = this.formatName(item);
                            this.attendeeInfo[attendeeId] = item;
                            if (isSelf) {
                                this.selfAttendeeInfo = item;
                                this.displayName = item["displayName"];
                            }
                            for (let i = 0; i < this.messages.length; i++) {
                                const message = this.messages[i];
                                if (message && message.id == attendeeId) {
                                    message.name = item["displayName"];
                                }
                            }
                            for (const key in this.roster) {
                                const who = this.roster[key];
                                if (
                                    who.externalUserId &&
                                    who.externalUserId.split(":")[0] ==
                                        attendeeId
                                ) {
                                    who.info = item;
                                }
                            }

                            return resolve();
                        } catch (error) {
                            console.error("ERROR in attendee handler: ", error);
                        }
                    })
                    .catch(error => {
                        return reject(error);
                    });
            });
        },
        formatName(info) {
            if (info === undefined) {
                return this.unknownName;
            }
            if (info["firstName"] && info["lastName"])
                return info["firstName"] + " " + info["lastName"];
            else if (info["firstName"]) return info["firstName"];
            else return info["lastName"] ? info["lastName"] : "Unknown";
        },
        setupAttendeePresenceHandler() {
            const presenceHandler = async (
                attendeeId,
                present,
                externalUserId,
                dropped,
                posInFrame
            ) => {
                try {
                    const selfAttendeeId = this.chime.meetingSession
                        .configuration.credentials.attendeeId;
                    let isContent = false;

                    if (attendeeId.indexOf("#") >= 0) {
                        attendeeId = attendeeId.split("#")[0];
                        isContent = true;
                        if (!present && this.myContentIsSharing)
                            this.toggleContentShare();
                        return;
                    }

                    if (!present) {
                        this.removeElement(this.videoAttendees, attendeeId);
                        this.removeElement(this.audioAttendees, attendeeId);
                        this.removeElement(this.silentAttendees, attendeeId);
                        this.$delete(this.roster, attendeeId);
                        this.$rollbar.warning(
                            `Attendee left: DROPPED:${dropped} AID:${attendeeId} EUID:${externalUserId} MEETING:${this.meetingId} SID:${this.$route.params.sessionId}`
                        );
                        return;
                    }

                    if (this.attendeeInfo[attendeeId] === undefined) {
                        this.$rollbar.warning(
                            `Attendee joined: AID:${attendeeId} EUID:${externalUserId} MEETING:${this.meetingId} SID:${this.$route.params.sessionId}`
                        );
                        try {
                            await this.getAttendeeData(
                                externalUserId.split(":")[0],
                                attendeeId == selfAttendeeId
                            );
                        } catch (error) {
                            console.error(error);
                        }
                    }

                    // roster: attendeeId -> {
                    //             externalUserId,
                    //             info
                    //             video   HTMLVideoElement (Bound if active)
                    // }
                    // TODO dont add roster item if externalUserId already exists in roster
                    if (!this.roster[attendeeId]) {
                        if (
                            !this.isPresentation ||
                            this.attendeeIsPresenter(attendeeId)
                        ) {
                            this.addAudioMember(attendeeId);
                        } else {
                            this.addSilentMember(attendeeId);
                        }
                        this.$set(this.roster, attendeeId, {});
                    }

                    if (!this.roster[attendeeId].externalUserId) {
                        this.$set(
                            this.roster[attendeeId],
                            "externalUserId",
                            externalUserId
                        );
                    }

                    if (!this.roster[attendeeId].info) {
                        this.$set(
                            this.roster[attendeeId],
                            "info",
                            this.attendeeInfo[externalUserId.split(":")[0]]
                        );
                    }

                    if (!this.roster[attendeeId].video) {
                        this.$set(
                            this.roster[attendeeId],
                            "video",
                            this.getStaticElement(attendeeId)
                        );
                    }

                    this.chime.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
                        attendeeId,
                        async (attendeeId, volume, muted, signalStrength) => {
                            try {
                                if (!this.roster[attendeeId]) {
                                    return;
                                }
                                if (volume !== null) {
                                    this.$set(
                                        this.roster[attendeeId],
                                        "volume",
                                        Math.round(volume * 100)
                                    );
                                }
                                if (muted !== null) {
                                    this.$set(
                                        this.roster[attendeeId],
                                        "muted",
                                        muted
                                    );
                                }
                                if (signalStrength !== null) {
                                    this.$set(
                                        this.roster[attendeeId],
                                        "signalStrength",
                                        Math.round(signalStrength * 100)
                                    );
                                }
                            } catch (error) {
                                console.error(
                                    "Error in volume callback: ",
                                    error
                                );
                            }
                        }
                    );
                } catch (error) {
                    console.error("ERROR in presence handler: ", error);
                }
            };
            this.chime.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
                presenceHandler
            );
        },
        initializeVideo: async function() {
            let videoDevice = localStorage["preferredVideoInput"];

            if (videoDevice === undefined) {
                // FIXME: or is deleted...
                videoDevice = this.videoInputs[0].deviceId;
            }

            await this.setVideoInput(videoDevice);
        },
        setupChime: async function() {
            // setupCanUnmuteHandler FIXME

            this.messageTopics.forEach(topic => {
                this.chime.meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
                    topic,
                    dataMessage => {
                        this.dataMessageHandler(dataMessage);
                    }
                );
            });

            if (!this.isPresentation || this.isMeetingPresenter) {
                let audioInputDevice = localStorage["preferredAudioInput"];
                if (audioInputDevice === undefined) {
                    // FIXME: or is deleted...
                    audioInputDevice = this.audioInputs[0].deviceId;
                }
                await this.setAudioInput(audioInputDevice);
                await this.initializeVideo();
                await this.setMuted();
            }
            let audioOutputDevice = localStorage["preferredAudioOutput"];
            if (audioOutputDevice === undefined) {
                // FIXME: or is deleted...
                audioOutputDevice =
                    this.audioOutputs.length == 0
                        ? undefined
                        : this.audioOutputs[0].deviceId;
            }
            await this.setAudioOutput(audioOutputDevice);

            const meetingSession = window.meetingstuff.chime.meetingSession;
            const observer = {
                // videoTileDidUpdate is called whenever a new tile is created or tileState changes.
                videoTileDidUpdate: async tileState => {
                    try {
                        const rosterItemToAdd = {};

                        if (!tileState.boundAttendeeId) {
                            return;
                        }
                        if (tileState.active) {
                            this.addVideoMember(tileState.boundAttendeeId);
                        } else {
                            this.addAudioMember(tileState.boundAttendeeId);
                        }
                        if (tileState.localTile && !tileState.isContent) {
                            this.myTile = tileState;
                        }
                        const selfAttendeeId = this.chime.meetingSession
                            .configuration.credentials.attendeeId;
                        if (
                            tileState.boundAttendeeId.split("#")[0] ==
                                selfAttendeeId &&
                            tileState.isContent
                        ) {
                            // don't bind one's own content
                            return;
                        }

                        if (this.currentSpeaker === undefined) {
                            this.currentSpeaker = tileState.boundAttendeeId;
                        }
                        let newElement = tileState.boundVideoElement;
                        if (!(newElement instanceof HTMLVideoElement)) {
                            if (tileState.isContent) {
                                newElement = undefined;
                            }

                            if (this.roster[tileState.boundAttendeeId]) {
                                newElement = this.roster[
                                    tileState.boundAttendeeId
                                ].video;
                            }

                            if (!(newElement instanceof HTMLVideoElement)) {
                                newElement = document.createElement("video");
                                newElement["id"] =
                                    "id-" + tileState.boundAttendeeId;
                                newElement["autoplay"] = "true";
                                newElement.className =
                                    "video-thumbnail swiper-slide";
                            }

                            if (newElement instanceof HTMLVideoElement) {
                                try {
                                    await this.chime.meetingSession.audioVideo.bindVideoElement(
                                        tileState.tileId,
                                        newElement
                                    );
                                } catch (error) {
                                    console.log(
                                        "Can't bind video element: ",
                                        tileState,
                                        newElement
                                    );
                                }
                            }
                        }

                        if (tileState.isContent && this.contentTile === null) {
                            newElement.className =
                                "absolute top-0 left-0 w-full h-full object-fit";
                            this.contentTile = tileState;
                            this.contentTile.boundVideoElement = newElement;
                            this.nodeToShow = this.currentSpeakerEl = newElement;
                            this.gridViewWasActive = this.gridViewIsActive;
                            this.gridViewIsActive = false;
                        }

                        rosterItemToAdd.video = newElement;

                        const upPromise = this.chime.meetingSession.audioVideo.unpauseVideoTile(
                            tileState.tileId
                        );
                        if (upPromise !== undefined) upPromise.then().catch();

                        this.$set(this.videoFeeds, tileState.tileId, tileState);
                        if (
                            Object.keys(this.videoFeeds).length >=
                            this.maxVideoFeeds
                        ) {
                            this.canAddVideo = false;
                        }

                        // Finally $set the roster item
                        if (
                            !this.roster[tileState.boundAttendeeId] &&
                            !tileState.isContent
                        ) {
                            this.addVideoMember(tileState.boundAttendeeId);
                            this.$set(
                                this.roster,
                                tileState.boundAttendeeId,
                                rosterItemToAdd
                            );
                        }
                    } catch (error) {
                        console.error("Error handling video tile", error);
                    }
                },
                videoTileWasRemoved: tileId => {
                    try {
                        if (
                            this.contentTile &&
                            this.contentTile.tileId == tileId
                        ) {
                            // dont delete data() item - reset to original value
                            this.contentTile = null;
                            if (this.myTile) {
                                this.currentSpeakerEl = this.nodeToShow = this.myTile.boundVideoElement;
                            }
                            this.gridViewIsActive = this.gridViewWasActive;
                        }
                        if (!this.videoFeeds[tileId]) return;
                        const aid = this.videoFeeds[tileId].boundAttendeeId;
                        if (
                            this.isPresentation &&
                            !this.attendeeIsPresenter(aid)
                        ) {
                            this.addSilentMember(aid);
                        } else {
                            this.addAudioMember(aid);
                        }
                        this.$delete(this.videoFeeds, tileId);
                        if (
                            Object.keys(this.videoFeeds).length <
                            this.maxVideoFeeds
                        ) {
                            if (this.isPresentation) {
                                this.canAddVideo =
                                    this.isMeetingPresenter || this.isPromoted;
                            } else {
                                this.canAddVideo = true;
                            }
                        }
                    } catch (error) {
                        console.error("Error removing video tile: ", error);
                    }
                },
                videoAvailabilityDidChange: availability => {
                    try {
                        this.canStartLocalVideo =
                            availability.canStartLocalVideo;
                        if (this.canStartLocalVideo) {
                            if (!this.cameraOffSelect) {
                                this.cameraOn = true;
                                this.chime.meetingSession.audioVideo.startLocalVideoTile();
                            }
                        }
                        this.remoteVideoAvailable =
                            availability.remoteVideoAvailable;
                    } catch (error) {
                        console.error(
                            "Error changing video availability: ",
                            error
                        );
                    }
                },
                videoSendDidBecomeUnavailable: () => {
                    this.cameraOn = false;
                    console.log("videoSendDidBecomeUnavailable");
                }
            };

            this.chime.meetingSession.audioVideo.addObserver(observer);
            this.chime.meetingSession.audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(
                muted => {
                    this.muted = muted;
                }
            );
            this.setupAttendeePresenceHandler();
            meetingSession.audioVideo.subscribeToActiveSpeakerDetector(
                new DefaultActiveSpeakerPolicy(),
                this.activeSpeakerHandler
            );
            await this.chime.meetingSession.audioVideo.start();
            //this.chime.meetingSession.audioVideo.startLocalVideoTile();

            // Finally, do this last
            this.meetingHasConnection = true;
        },
        getStaticElement(attendeeId) {
            const newElement = document.createElement("video");
            newElement["id"] = "id-" + attendeeId;
            newElement.className = "video-thumbnail swiper-slide";
            return newElement;
        },
        async cleanupChime() {
            if (this?.chime?.meetingSession) {
                await this.chime.meetingSession.audioVideo.chooseVideoInputDevice(
                    null
                );
                await this.chime.meetingSession.audioVideo.stop();
                if (this.myContentIsSharing) {
                    this.stopContentShare();
                }
            }
            this.roster = {};
        },
        leaveMeeting() {
            // Chime clean up
            this.cleanupChime();

            // logging
            if (this.meetingIsOver) {
                this.logKickedOutEvent();
            }

            const scheduleNavItem = this.getNavItem("Schedule");
            const sessionsNavItem = this.getNavItem("Sessions");
            let redirectTo = "/";

            if (scheduleNavItem && scheduleNavItem.active) {
                redirectTo = scheduleNavItem.path;
            } else if (sessionsNavItem && sessionsNavItem.active) {
                redirectTo = sessionsNavItem.path;
            }

            // finally, leave
            this.$router.push(redirectTo);
        },
        goToSettingsPage(page) {
            this.settingsPage = page;
        },
        goToSecondarySettingsPage(page) {
            this.secondarySettingsPage = page;
        },
        sendMessageToUser(msg, target) {
            this.sendMessage(msg, "direct/" + target);
        },
        sendMessage(msg, topic = "chat") {
            new AsyncScheduler().start(() => {
                msg = msg.trim();

                if (!msg) {
                    return;
                }
                this.chime.meetingSession.audioVideo.realtimeSendDataMessage(
                    topic,
                    msg,
                    this.DATA_MESSAGE_LIFETIME_MS
                );
                // echo the message to the handler
                this.dataMessageHandler(
                    new DataMessage(
                        Date.now(),
                        topic,
                        new TextEncoder().encode(msg),
                        this.chime.meetingSession.configuration.credentials.attendeeId,
                        this.chime.meetingSession.configuration.credentials.externalUserId
                    )
                );
            });
        },
        async dataMessageHandler(dataMessage) {
            if (!dataMessage.throttled) {
                //const isSelf = dataMessage.senderAttendeeId === this.meetingSession.configuration.credentials.attendeeId;
                /* if (
                    dataMessage.timestampMs <= this.lastReceivedMessageTimestamp
                ) {
                    return;
                }*/
                const selfAttendeeId = this.chime.meetingSession.configuration
                    .credentials.attendeeId;
                const attendeeId = dataMessage.senderExternalUserId.split(
                    ":"
                )[0];
                if (attendeeId === undefined) {
                    try {
                        await this.getAttendeeData(
                            attendeeId,
                            dataMessage.senderAttendeeId ==
                                this.chime.meetingSession.configuration
                                    .credentials.attendeeId
                        );
                    } catch (error) {
                        console.error(error);
                    }
                }

                // TODO
                // we'll need to subscribe to topics labeled as: "direct/" + id

                if (
                    dataMessage.topic == "chat" ||
                    dataMessage.topic == "direct/" + selfAttendeeId
                ) {
                    this.lastReceivedMessageTimestamp = dataMessage.timestampMs;
                    this.lastMessageSender = dataMessage.senderAttendeeId;
                    const toFormat = this.attendeeInfo[attendeeId];

                    const message = {
                        name: this.formatName(toFormat),
                        message: dataMessage.text(),
                        time: new Date(dataMessage.timestampMs).toISOString(),
                        id: attendeeId
                    };

                    if (dataMessage.text()) {
                        this.messages.push(message);
                    }
                } else if (dataMessage.topic == "raisehand") {
                    this.handleRaiseHand(dataMessage.senderAttendeeId);
                } else if (dataMessage.topic == "lowerhand") {
                    this.handleLowerHand(dataMessage.senderAttendeeId);
                } else if (dataMessage.topic == "promote") {
                    /* FIXME: Verify that the sender attendee is a presenter */
                    this.handlePromoteAttendee(dataMessage.text());
                } else if (dataMessage.topic == "demote") {
                    /* FIXME: Verify that the sender attendee is a presenter */
                    this.handleDemoteAttendee(dataMessage.text());
                }
            } else {
                this.log("Message is throttled. Please resend");
            }
        },
        toggleRaiseHand() {
            this.handIsRaised = !this.handIsRaised;

            if (this.handIsRaised) {
                this.requestRaiseHand();
            } else {
                this.requestLowerHand();
            }
        },
        requestRaiseHand() {
            this.sendMessage(this.getSelfAttendeeId(), "raisehand");
        },
        handleRaiseHand(senderAttendeeId) {
            if (!this.raisedHands.includes(senderAttendeeId)) {
                this.raisedHands.push(senderAttendeeId);
            }
        },
        requestLowerHand() {
            this.sendMessage(this.getSelfAttendeeId(), "lowerhand");
        },
        handleLowerHand(senderAttendeeId) {
            this.handleDemoteAttendee(senderAttendeeId);
            this.removeElement(this.raisedHands, senderAttendeeId);
        },
        /* This should be called when a Presenter clicks on a button to promote someone */
        requestPromotion(attendeeId) {
            this.sendMessage(attendeeId, "promote");
        },
        /* This should be called when we get the promotion datamessage only */
        async handlePromoteAttendee(promoteAttendeeId) {
            const selfAttendeeId = this.chime.meetingSession.configuration
                .credentials.attendeeId;
            this.promotedFolks.push(promoteAttendeeId);
            if (promoteAttendeeId == selfAttendeeId) {
                // init video before adding tile
                await this.initializeVideo();

                this.isPromoted = true;

                // Unmute when promoted; they should be able to mute if
                // required
                if (this.muted) {
                    this.setMuted();
                }
            }
        },
        /* This should be called when a Presenter clicks on a button to demote someone */
        requestDemoteAttendee(demoteAttendeeId) {
            this.sendMessage(demoteAttendeeId, "demote");
        },
        /* This should be called when we get the demotion dataMessage only */
        handleDemoteAttendee(demoteAttendeeId) {
            const selfAttendeeId = this.chime.meetingSession.configuration
                .credentials.attendeeId;
            this.removeElement(this.promotedFolks, demoteAttendeeId);
            if (demoteAttendeeId == selfAttendeeId) {
                this.isPromoted = false;
                // Mute when promoted; they should be able to mute if
                // required
                if (!this.muted) {
                    this.setMuted();
                }
                if (this.cameraOn) {
                    this.toggleVideo();
                }
            }
        },

        sendChatMessage() {
            this.sendMessage(
                linkifyHtml(this.message, {
                    attributes: {
                        rel: "noopener noreferrer"
                    }
                })
            );

            this.message = "";
            this.$nextTick(() => {
                const chatScroll = this.$refs["chat-scroll"];
                if (chatScroll) {
                    // scroll to bottom to see submitted message
                    chatScroll.scrollTo(null, chatScroll.scrollHeight);
                }
            });
        },
        setupDeviceLabelTrigger() {
            this.chime.meetingSession.audioVideo.setDeviceLabelTrigger(
                async () => {
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia(
                            {
                                audio: true,
                                video: true
                            }
                        );
                        return stream;
                    } catch (error) {
                        console.error("Unable to getUserMedia: ", error);
                    }
                }
            );
        },
        handleControlPermissions() {
            if (this.isPresentation) {
                this.canAddAudio = this.isMeetingPresenter || this.isPromoted;
                this.canAddVideo = this.isMeetingPresenter || this.isPromoted;
            } else {
                this.canAddVideo = true;
                this.canAddAudio = true;
            }
        },
        async init() {
            const sessionId = this.$route.params.sessionId;
            // const userId = this.$store.getters.user.id;

            this.setInitialBrandColor();

            if (this.$store.getters.user === undefined) {
                console.log("Bad USER");
            }

            this.chime = Object();

            this.chime.logger = new ConsoleLogger(
                "ChimeMeetingLogs",
                LogLevel.WARN
            );
            this.chime.deviceController = new DefaultDeviceController(
                this.chime.logger
            );

            let attendeePromise;
            let meetingRoomPromise;

            try {
                // Fetch in Parallel to speed things up
                attendeePromise = this.getAttendeeData(
                    this.$store.getters.user.id,
                    true
                );
                meetingRoomPromise = apiSvc.getMeetingRoom(sessionId);

                // do something with results.
                await attendeePromise; //nothing to do with this.

                const meetingRoomData = await meetingRoomPromise;

                this.meetingApiData = meetingRoomData.data;
            } catch (error) {
                console.error(error);
                // TODO might make sense to throw here. Not sure.
                // if( !this.meetingApiData ){
                // 	throw new Error('Failed to get meeting data')
                // }
            }

            this.$rollbar.warning(
                `Enter meeting, ID ${this.$store.getters.user.id},
                MeetingID: ${this.meetingApiData["meeting"]["MeetingId"]},
                AttendeeID: ${this.meetingApiData["attendee"]["AttendeeId"]}`
            );
            this.meetingId = this.meetingApiData["meeting"]["MeetingId"];

            this.chime.attendee = this.meetingApiData["attendee"];
            this.chime.meeting = this.meetingApiData["meeting"];

            this.chime.configuration = new MeetingSessionConfiguration(
                this.chime.meeting,
                this.chime.attendee
            );
            this.chime.configuration.enableUnifiedPlanForChromiumBasedBrowsers = true;
            this.chime.configuration.enableWebAudio = false;
            this.chime.meetingSession = new DefaultMeetingSession(
                this.chime.configuration,
                this.chime.logger,
                this.chime.deviceController
            );

            this.chime.deviceController.addDeviceChangeObserver(this);
            // FIXME: setupDeviceLabelTrigger
            this.setupDeviceLabelTrigger();
            await this.populateAllDeviceLists();

            await this.setupChime();

            // decide muted
            if (this.isPresentation) {
                this.muted = !this.isMeetingPresenter || !this.isPromoted;
                if (this.muted) {
                    this.chime.meetingSession.audioVideo.realtimeMuteLocalAudio();
                }
            }

            this.handleControlPermissions();
        },
        async populateAllDeviceLists() {
            this.audioInputs = await this.chime.meetingSession.audioVideo.listAudioInputDevices();
            this.audioOutputs = await this.chime.meetingSession.audioVideo.listAudioOutputDevices();
            this.videoInputs = await this.chime.meetingSession.audioVideo.listVideoInputDevices();
        },
        countMeetingTime() {
            if (0 < this.meetingSecondsInitial) {
                this.secondsCountDown = this.meetingSecondsInitial;
            }

            this.meetingSecondsDisplay = "00";

            function pad(val) {
                const valString = val + "";
                if (valString.length < 2) {
                    return "0" + valString;
                } else {
                    return valString;
                }
            }

            const setTime = () => {
                this.meetingMinutesDisplay = pad(
                    parseInt(this.secondsCountDown / 60)
                );
                this.meetingSecondsDisplay = pad(this.secondsCountDown % 60);

                this.setMeetingTimeRemaining();

                if (
                    this.extraMinutesActive &&
                    (this.secondsCountDown <= 0 ||
                        this.extraSecondsOvertime > this.timeRemaining)
                ) {
                    clearInterval(this.meetingCount);
                    this.meetingMinutesDisplay == "00";
                    this.meetingSecondsDisplay == "00";
                    this.meetingIsOver = true;
                } else if (this.secondsCountDown <= 0) {
                    this.secondsCountDown = this.extraSeconds;
                    this.extraMinutesActive = true;
                } else if (0 <= this.timeRemaining) {
                    this.secondsCountDown = this.timeRemaining;
                } else {
                    --this.secondsCountDown;
                }
            };

            this.meetingCount = setInterval(setTime, 1000);
        },
        async handleLogUsage() {
            this.logInterval = window.setInterval(() => {
                const logData = {
                    type: "MeetingUsage",
                    uid: this.$store.getters.user.id,
                    attendeeID: this.chime.attendee,
                    meetingID: this.chime.meeting["MeetingId"],
                    sessionID: this.$route.params.sessionId
                };
                const logReturn = apiSvc
                    .appendLogEntry(logData)
                    .then(response => {
                        return response.data;
                    })
                    .catch(error => {
                        console.error(
                            "Error trying to log: ",
                            logData,
                            error,
                            error.stack
                        );
                    });
            }, 60 * 1000);
        },
        handleMeetingTime() {
            // first set meeting seconds
            let timeToSet = 0;

            if (this.meetingData && this.meetingData.startTimeISO) {
                this.setMeetingTimeRemaining();
                timeToSet = this.timeRemaining;
            } else {
                timeToSet = DEFAULT_MEETING_DURATION;
            }

            this.meetingSecondsInitial = timeToSet;

            // then start countdown
            this.countMeetingTime();
        },
        handleTertiaryPanel(pageWanted = null) {
            const tertiaryPanel = "mg-control-panel--tertiary";

            if (this.isTertiaryPanelActive) {
                if (pageWanted === this.secondarySettingsPage) {
                    this.toggleUiElement(tertiaryPanel);
                } else {
                    // nothing to do here
                }
            } else {
                this.toggleUiElement(tertiaryPanel);
            }

            this.goToSecondarySettingsPage(pageWanted);
        },
        isAttendeePromoted(id = "") {
            return Boolean(this.promotedFolks.includes(id));
        },
        addRosterItem() {
            // this is here for debugging
            this.$set(this.roster, new Date().toISOString(), {
                video: {},
                externalUserId:
                    "34A13D00000003:fa7d40bb-e37e-4899-8be5-db76f3f40ecf",
                info: {
                    companyId: "2BD50001D2CB",
                    company: {
                        zip: "24210-1100",
                        country: "United States",
                        linkedInURL:
                            '<a target=_blank href="http://www.twitter.com/FoodCity">FoodCity</a>',
                        city: "Abingdon",
                        address1: "1 Food City Circle",
                        websiteURLs: ["www.foodcity.com"],
                        twitterURL:
                            '<a target=_blank href="http://www.twitter.com/FoodCity">FoodCity</a>',
                        isExhibitor: true,
                        companyId: "2BD50001D2CB",
                        facebookURL:
                            '<a target=_blank href="http://www.facebook.com/FoodCity/">FoodCity</a>',
                        phone: "D276|623|5100|",
                        name: "K-VA-T Food Stores, Inc.",
                        logo:
                            "http://intranet.fmiam.maxx.matrixdev.net/uploadFiles/2BD50001D2CB.logo.KVAT-FoodCity-Logo-1024x291.jpg",
                        state: "VA"
                    },
                    lastName: "Jefferson",
                    companyName: "K-VA-T Food Stores, Inc.",
                    attendeeId: "34A13D00000003",
                    prefix: "Mr.",
                    firstName: "Thomas",
                    title: "President, USA",
                    displayName: "Thomas Jefferson"
                }
            });
        }
    }
};
</script>

<style lang="scss">
@import "../styles/views/meeting";
</style>
